import { useCallback, useRef, useState } from 'react';

import type { DraftEntity } from '@/lib/crm/core/draftEntities';

/**
 * Pure collector: given the full draft map and a set of associations, return
 * the drafts referenced as association targets. Extracted so submit-time
 * logic and tests don't need a hook instance.
 */
export function selectReferencedDrafts(
  drafts: Record<string, DraftEntity>,
  associations: ReadonlyArray<{ target_entity_id: string }>
): DraftEntity[] {
  const referenced = new Set(associations.map(a => a.target_entity_id));
  return Object.values(drafts).filter(d => referenced.has(d.entity_id));
}

/**
 * Holds the draft map (entity_id -> DraftEntity) for nested-create flows.
 * `drafts` is for render-time reads; `collectReferencedDrafts` reads a
 * ref-mirror so submit-time callers get the latest drafts without depending
 * on `drafts`, so a submit handler built before the last draft landed still
 * folds it in.
 */
export function useDraftEntities() {
  const [drafts, setDrafts] = useState<Record<string, DraftEntity>>({});
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  const addDraft = useCallback((d: DraftEntity) => {
    setDrafts(prev => ({ ...prev, [d.entity_id]: d }));
  }, []);

  const clearDrafts = useCallback(() => setDrafts({}), []);

  const collectReferencedDrafts = useCallback(
    (associations: ReadonlyArray<{ target_entity_id: string }>) =>
      selectReferencedDrafts(draftsRef.current, associations),
    []
  );

  return { drafts, addDraft, clearDrafts, collectReferencedDrafts };
}
